import {
  SELECT_TYPE,
  DESC_KEYS,
  CALENDAR_ITEM_COLOR,
} from '@/constants/calendar';
import { useQueryCalendar } from '@/query/calendar/useQueryCalendar';
import useCalendarStore from '@/store/calendarStore';
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

const useInitCalendarData = (currentViewDate?: Date) => {
  const [searchParams] = useSearchParams();
  const [fetchedMonths, setFetchedMonths] = useState<Set<string>>(new Set());

  const targetDate = useMemo(() => {
    if (currentViewDate) {
      return currentViewDate;
    } else {
      const year = searchParams.get('year')
        ? Number(searchParams.get('year'))
        : new Date().getFullYear();
      const month = searchParams.get('month')
        ? Number(searchParams.get('month'))
        : new Date().getMonth();
      return new Date(year, month - 1);
    }
  }, [currentViewDate, searchParams]);

  const monthKey = useMemo(
    () => dayjs(targetDate).format('YYYY-MM'),
    [targetDate]
  );

  const shouldFetch = currentViewDate ? true : !fetchedMonths.has(monthKey);

  const { data: globalEvents, isLoading: globalLoading } = useQueryCalendar(
    SELECT_TYPE[0],
    '',
    targetDate,
    shouldFetch
  );
  const { data: koreaEvents, isLoading: koreaLoading } = useQueryCalendar(
    SELECT_TYPE[1],
    '',
    targetDate,
    shouldFetch
  );

  const { checkedEvent, setEvents, setIsLoading } = useCalendarStore();

  useEffect(() => {
    if (shouldFetch) {
      setIsLoading(true);
    }
  }, [shouldFetch]);

  useEffect(() => {
    if (!shouldFetch) return;

    if (globalLoading || koreaLoading) {
      setIsLoading(true);
      return;
    }

    if (!currentViewDate) {
      setFetchedMonths(prev => new Set(Array.from(prev).concat(monthKey)));
    }

    if (globalEvents && globalEvents.length > 0) {
      initEvents(globalEvents, 'GLOBAL');
    }

    if (koreaEvents && koreaEvents.length > 0) {
      initEvents(koreaEvents, 'KOREA');
    }

    setIsLoading(false);
  }, [
    globalEvents,
    koreaEvents,
    globalLoading,
    koreaLoading,
    shouldFetch,
    monthKey,
    currentViewDate,
  ]);

  const initEvents = (events: any[], type: string) => {
    const parsedEvents = events.map(event => parseEvents(event));
    const color = CALENDAR_ITEM_COLOR[type];
    const eventsWithColor = parsedEvents.map(event => ({
      ...event,
      backgroundColor: color,
      borderColor: color,
    }));

    const { events: existingEvents } = useCalendarStore.getState();
    const existingEventsForType = existingEvents[type] || [];

    const newEvents = eventsWithColor.filter(
      newEvent =>
        !existingEventsForType.some(existing => existing.id === newEvent.id)
    );

    const mergedEvents = existingEventsForType.concat(newEvents);
    setEvents(mergedEvents, type);
  };
};

export default useInitCalendarData;

export const filterEvents = (events, setEvents, filter) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  if (events.length === 0) {
    setEvents([]);
    return;
  }

  const filtered = events.filter(event => {
    const eventEndDate = new Date(event.end);

    if (!filter || filter === 'Upcoming') {
      return eventEndDate > now;
    } else if (filter === 'Month') {
      const isEventInMonth =
        eventEndDate.getFullYear() === currentYear &&
        eventEndDate.getMonth() === currentMonth;
      const isAllDayAndEndFirstDay =
        event.allDay && eventEndDate.getDate() === 1;

      return isEventInMonth && !isAllDayAndEndFirstDay;
    } else if (filter === 'Year') {
      return eventEndDate.getFullYear() === currentYear;
    }

    return false;
  });

  setEvents(filtered);
};

const parseEvents = event => {
  event = Object.entries(event).reduce((acc, [key, value]) => {
    return { ...acc, [key]: !!value ? value : '' };
  }, {});

  let parsedDescription;
  if (event.description) {
    parsedDescription = parseDescription(event.description);
  }

  return {
    ...event,
    description: { ...parsedDescription },
  };
};

function convertBrToNewlineAndStripHtmlTags(str) {
  // <br> 태그를 줄바꿈 문자로 변경
  let result = str.replace(/<br\s*\/?>/gi, '\n');

  // 모든 HTML 태그를 제거
  result = result.replace(/<\/?[^>]+(>|$)/g, '');

  return result;
}

const parseDescription = description => {
  const formattedDescription = convertBrToNewlineAndStripHtmlTags(description);
  const lines: string[] = formattedDescription.split('\n');

  const descIndex = lines.findIndex(line => line.indexOf('Description') !== -1);

  if (descIndex === -1) {
    return {};
  }
  const informations = lines.slice(0, descIndex);
  const formattedInformations = informations.map(info => {
    const colonIndex = info.indexOf(':');
    if (colonIndex !== -1) {
      let key = info.slice(0, colonIndex).trim();
      let value: string | string[] = info.slice(colonIndex + 1).trim();

      if (key === 'Speakers') {
        value = customSplit(value);
      } else if (value.length === 1) {
        value = value[0];
      }

      return { key, value };
    }
  });

  const descFirstLine = lines[descIndex].split('Description:')[1];
  const descSecondLine = lines.slice(descIndex + 1).join('\n');

  const desc = (descFirstLine + '\n' + descSecondLine).trim();

  const resultList = [
    ...formattedInformations,
    { key: 'Description', value: desc },
  ];

  return resultList.reduce((acc, curr) => {
    const descKey = Object.entries(DESC_KEYS).filter(
      ([key, value]) => value === curr.key
    );
    if (descKey.length === 0) {
      return acc;
    }

    const key = descKey[0][0];

    acc[key] = curr.value;

    return acc;
  }, {});
};

function customSplit(str) {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let inParentheses = 0;

  // 문자열의 앞뒤 대괄호와 작은 따옴표를 제거
  const trimmedStr = str.replace(/\[|\]/g, '');

  for (let i = 0; i < trimmedStr.length; i++) {
    const char = trimmedStr[i];

    if (char === "'") {
      inQuotes = !inQuotes;
      current += char;
    } else if (char === '(') {
      inParentheses++;
      current += char;
    } else if (char === ')') {
      inParentheses--;
      current += char;
    } else if (char === ',' && !inQuotes && inParentheses === 0) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current) {
    result.push(current.trim());
  }

  return result.map(item => item.replace(/^'|'$/g, '').trim());
}
